import { z } from 'zod/v4';
import { RegistryIdentifierSchema } from '../customers/index.js';
import { Currency } from '../index.js';
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export var LienType;
(function (LienType) {
    LienType["Attachment"] = "ATTACHMENT";
    LienType["Mortgage"] = "MORTGAGE";
    LienType["OwnerNote"] = "OWNER_NOTE";
    LienType["PrivateNote"] = "PRIVATE_NOTE";
    LienType["PrivateIndexLinkedMortgage"] = "PRIVATE_INDEX_LINKED_MORTGAGE";
    LienType["Other"] = "OTHER";
    LienType["Unknown"] = "UNKNOWN";
})(LienType || (LienType = {}));
export var InterestType;
(function (InterestType) {
    InterestType["Variable"] = "VARIABLE";
    InterestType["Fixed"] = "FIXED";
})(InterestType || (InterestType = {}));
export var ValuationSource;
(function (ValuationSource) {
    ValuationSource["PublicAssessment"] = "PUBLIC_ASSESSMENT";
    ValuationSource["LastSale"] = "LAST_SALE";
    ValuationSource["Manual"] = "MANUAL";
})(ValuationSource || (ValuationSource = {}));
export var AddressRegister;
(function (AddressRegister) {
    AddressRegister["DAR"] = "DK:DAR";
})(AddressRegister || (AddressRegister = {}));
// ---------------------------------------------------------------------------
// Transfer methods
// ---------------------------------------------------------------------------
const GENERIC_TRANSFER_METHODS = [
    'FREE_SALE',
    'FORCED_AUCTION',
    'INHERITANCE',
    'GIFT',
    'OTHER',
];
const REAL_ESTATE_TRANSFER_METHODS = [
    ...GENERIC_TRANSFER_METHODS,
    'FAMILY_TRANSFER',
    'EXCHANGE',
];
// ---------------------------------------------------------------------------
// Shared value schemas
// ---------------------------------------------------------------------------
export const CosmoAddressValueSchema = z
    .object({
    address: z.string(),
    zipcode: z.string(),
    city: z.string(),
    coAddress: z.string().optional(),
    country: z.string().optional(),
    municipalityCode: z.string().optional(),
    roadCode: z.string().optional(),
    moveInDate: z.iso.date().optional(),
    registerIds: z
        .object({
        [AddressRegister.DAR]: z.uuid().optional(),
    })
        .optional(),
})
    .nullable();
export const CosmoPartySchema = z.object({
    name: z.string().optional(),
    registryIdentifier: RegistryIdentifierSchema.optional(),
    birthdate: z.iso.date().optional(),
});
export const CosmoAssetOwnerSchema = CosmoPartySchema.extend({
    name: z.string(),
    birthdate: z.iso.date().optional(),
    share: z
        .object({
        numerator: z.number(),
        denominator: z.number(),
    })
        .optional(),
});
// ---------------------------------------------------------------------------
// Lien schemas
// ---------------------------------------------------------------------------
export const CosmoAssetLienOutstandingBalanceSchema = z.object({
    date: z.iso.date(),
    amount: z.number(),
});
export const CosmoAssetLienSchema = z.object({
    id: z.string(),
    type: z.enum(LienType),
    amount: z.number().optional(),
    currency: z.enum(Currency).optional(),
    registrationDate: z.iso.date().optional(),
    // Tinglysningsnummer (dato-løbenummer), e.g. 20220609-1013960682
    registrationReference: z.string().optional(),
    priority: z.number().optional(),
    creditor: CosmoPartySchema.optional(),
    debtors: z.array(CosmoPartySchema).optional(),
    interestRate: z.number().optional(),
    interestType: z.enum(InterestType).optional(),
    contributionRate: z.number().optional(),
    totalTermYears: z.number().optional(),
    interestOnlyYears: z.number().optional(),
    outstandingBalances: z
        .array(CosmoAssetLienOutstandingBalanceSchema)
        .optional(),
    source: z.string().optional(),
    debtorIsDebtor: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Transaction schemas
// ---------------------------------------------------------------------------
export const CosmoAssetTransactionSchema = z.object({
    transferDate: z.iso.date().optional(),
    transferMethod: z.enum(GENERIC_TRANSFER_METHODS).optional(),
    isConditional: z.boolean().optional(),
    totalPurchaseSum: z.number().optional(),
    currency: z.enum(Currency).optional(),
    transferId: z.string().optional(),
});
export const CosmoRealEstateTransactionSchema = CosmoAssetTransactionSchema.extend({
    transferMethod: z.enum(REAL_ESTATE_TRANSFER_METHODS).optional(),
});
// ---------------------------------------------------------------------------
// Valuation schemas
// ---------------------------------------------------------------------------
export const CosmoAssetValuationSchema = z.object({
    amount: z.number(),
    date: z.iso.date(),
    source: z.enum(ValuationSource),
    currency: z.enum(Currency).optional(),
});
export const VALUATION_SOURCE_PRIORITY = {
    [ValuationSource.Manual]: 0,
    [ValuationSource.PublicAssessment]: 1,
    [ValuationSource.LastSale]: 2,
};
export const getLatestValuation = (valuations) => {
    if (valuations == null || valuations.length === 0) {
        return null;
    }
    return valuations.toSorted((a, b) => {
        const dateCompare = b.date.localeCompare(a.date);
        if (dateCompare !== 0) {
            return dateCompare;
        }
        return (VALUATION_SOURCE_PRIORITY[a.source] - VALUATION_SOURCE_PRIORITY[b.source]);
    })[0];
};
// ---------------------------------------------------------------------------
// Asset schemas
// ---------------------------------------------------------------------------
export const MAX_ASSET_NOTE_LENGTH = 10_000;
const AssetNoteSchema = z.string().trim().max(MAX_ASSET_NOTE_LENGTH);
const CosmoAssetBaseSchema = z.object({
    id: z.string(),
    valuations: z.array(CosmoAssetValuationSchema).optional(),
    sources: z.array(z.string()).optional(),
    liens: z.array(CosmoAssetLienSchema).optional(),
    transactions: z.array(CosmoAssetTransactionSchema).optional(),
    debtorShare: z
        .object({
        numerator: z.number(),
        denominator: z.number(),
    })
        .optional(),
    note: AssetNoteSchema.optional(),
});
const RealEstateAssetSchema = CosmoAssetBaseSchema.extend({
    type: z.literal('REAL_ESTATE'),
    bfeNr: z.number().optional(),
    address: CosmoAddressValueSchema.optional(),
    owners: z.array(CosmoAssetOwnerSchema).optional(),
    administrator: CosmoPartySchema.optional(),
    transactions: z.array(CosmoRealEstateTransactionSchema).optional(),
});
const VehicleAssetSchema = CosmoAssetBaseSchema.extend({
    type: z.literal('VEHICLE'),
    // Stelnummer — the vehicle's natural key. Registration numbers are
    // reassigned across vehicles in Denmark, so only the VIN identifies one.
    vin: z.string().optional(),
    registrationNumber: z.string().optional(),
    registrationYear: z.string().optional(),
    brand: z.string().optional(),
    model: z.string().optional(),
    variant: z.string().optional(),
    liability: z
        .object({
        amount: z.number(),
        currency: z.string(),
    })
        .optional(),
});
const TaxAssetSchema = CosmoAssetBaseSchema.extend({
    type: z.literal('TAX'),
});
const OtherAssetSchema = CosmoAssetBaseSchema.extend({
    type: z.literal('OTHER'),
    description: z.string(),
});
export const CosmoAssetSchema = z.discriminatedUnion('type', [
    RealEstateAssetSchema,
    VehicleAssetSchema,
    TaxAssetSchema,
    OtherAssetSchema,
]);
// ---------------------------------------------------------------------------
// Input schemas (used by API consumers and frontend forms)
// ---------------------------------------------------------------------------
export const ManualAddressSchema = z.object({
    address: z.string().min(1),
    zipcode: z.string().min(1),
    city: z.string().min(1),
    coAddress: z.string().optional(),
    country: z.string().optional(),
});
const NewRealEstateAssetInputSchema = z.object({
    type: z.literal('REAL_ESTATE'),
    bfeNr: z.number().optional(),
    address: ManualAddressSchema.optional(),
    estimatedValue: z.number().optional(),
    currency: z.enum(Currency).optional(),
    note: AssetNoteSchema.optional(),
});
const NewVehicleAssetInputSchema = z.object({
    type: z.literal('VEHICLE'),
    registrationNumber: z.string().optional(),
    registrationYear: z.string().optional(),
    brand: z.string().optional(),
    model: z.string().optional(),
    variant: z.string().optional(),
    estimatedValue: z.number().optional(),
    currency: z.enum(Currency).optional(),
    note: AssetNoteSchema.optional(),
});
const NewTaxAssetInputSchema = z.object({
    type: z.literal('TAX'),
    estimatedValue: z.number().optional(),
    currency: z.enum(Currency).optional(),
    note: AssetNoteSchema.optional(),
});
const NewOtherAssetInputSchema = z.object({
    type: z.literal('OTHER'),
    description: z.string().min(1),
    estimatedValue: z.number().optional(),
    currency: z.enum(Currency).optional(),
    note: AssetNoteSchema.optional(),
});
export const CreateAssetInputSchema = z.discriminatedUnion('type', [
    NewRealEstateAssetInputSchema,
    NewVehicleAssetInputSchema,
    NewTaxAssetInputSchema,
    NewOtherAssetInputSchema,
]);
export const UpdateCosmoAssetInputSchema = z.object({
    estimatedValue: z.number().optional(),
    currency: z.enum(Currency).optional(),
    description: z.string().optional(),
    bfeNr: z.number().optional(),
    address: ManualAddressSchema.optional(),
    brand: z.string().optional(),
    model: z.string().optional(),
    variant: z.string().optional(),
    registrationNumber: z.string().optional(),
    registrationYear: z.string().optional(),
    note: AssetNoteSchema.optional(),
});
// Server-side patch: user-editable fields plus provider-enrichment fields
// (valuations to dedup-merge, sources to union). Frontend only ever sends the
// user-input subset above; provider parsers emit the full shape. Liens are
// deliberately not part of the patch: they arrive as per-lien ASSET-LIEN
// events, which the lien triggers (NewLien/LienChanged procedures) key on.
export const CosmoAssetUpdatePatchSchema = UpdateCosmoAssetInputSchema.extend({
    valuations: z.array(CosmoAssetValuationSchema).optional(),
    addSources: z.array(z.string()).optional(),
});
export const NewCosmoAssetLienInputSchema = z.object({
    type: z.enum(LienType),
    amount: z.number().optional(),
    currency: z.enum(Currency).optional(),
    registrationDate: z.iso.date().optional(),
    registrationReference: z.string().trim().min(1).optional(),
    priority: z.number().optional(),
    creditor: CosmoPartySchema,
    debtors: z.array(CosmoPartySchema).optional(),
    interestRate: z.number().optional(),
    interestType: z.enum(InterestType).optional(),
    contributionRate: z.number().optional(),
    totalTermYears: z.number().optional(),
    interestOnlyYears: z.number().optional(),
    outstandingBalances: z
        .array(CosmoAssetLienOutstandingBalanceSchema)
        .optional(),
});
export const UpdateCosmoAssetLienInputSchema = z.object({
    type: z.enum(LienType).optional(),
    amount: z.number().optional(),
    currency: z.enum(Currency).optional(),
    registrationDate: z.iso.date().optional(),
    priority: z.number().optional(),
    interestRate: z.number().optional(),
    interestType: z.enum(InterestType).optional(),
    contributionRate: z.number().optional(),
    totalTermYears: z.number().optional(),
    interestOnlyYears: z.number().optional(),
    outstandingBalances: z
        .array(CosmoAssetLienOutstandingBalanceSchema)
        .optional(),
});
//# sourceMappingURL=assets.js.map